// Touch controls for the game screen (shown in touch mode): twin nipplejs joysticks that appear where a thumb lands
// (left half moves, right half aims and auto-fires; a quick tap on the right attacks the way you face) and hold
// buttons for interact and block, plus chat and menu. Presses on the canvas UI never start a joystick.
import { create } from 'nipplejs';
import { app } from '../app';
import { touchMode } from '../ui/touch';

const UP = 1, DOWN = 2, LEFT = 4, RIGHT = 8, RUN = 16;
const DEADZONE = 0.2;
const RUN_FORCE = 0.9;
/** A move direction counts on an axis past this share of the stick (sin 22.5°): eight even sectors. */
const AXIS = 0.383;
const TAP_SECONDS = 0.25;

type Collection = ReturnType<typeof create>;
type Joystick = { uid: number; position: { x: number; y: number } };
type MoveData = { instance: Joystick; vector: { x: number; y: number }; force: number };
/** A thumb on a stick: its direction in screen space (y down) and how far it is pushed (0-1). */
interface Stick { uid: number; x: number; y: number; force: number; started: number; aimed: boolean }

export interface TouchActions { chat(): void; menu(): void }

export class TouchControls {
  private sticks: Collection | null = null;
  private left: Stick | null = null;
  private right: Stick | null = null;
  private readonly buttons: HTMLElement;
  private held = new Set<string>();
  private pressedNow = new Set<string>();
  private tapped = false;
  private readonly off: (() => void)[] = [];

  constructor(private readonly stage: HTMLElement, canvas: HTMLCanvasElement, actions: TouchActions) {
    // A touch on the canvas UI is the UI's: keep it from bubbling to the stage, where the joysticks listen.
    const guard = (e: PointerEvent) => { if (e.pointerType !== 'mouse' && app.ui.hitTest(e.clientX, e.clientY)) e.stopPropagation(); };
    canvas.addEventListener('pointerdown', guard);
    this.off.push(() => canvas.removeEventListener('pointerdown', guard));

    this.buttons = document.createElement('div');
    this.buttons.className = 'touch-buttons';
    const button = (label: string, hold: string | null, tap?: () => void) => {
      const b = document.createElement('button');
      b.textContent = label;
      b.addEventListener('pointerdown', e => {
        e.preventDefault(); e.stopPropagation();
        if (hold) { this.held.add(hold); this.pressedNow.add(hold); b.classList.add('down'); }
        tap?.();
      });
      const release = () => { if (hold) this.held.delete(hold); b.classList.remove('down'); };
      for (const type of ['pointerup', 'pointercancel', 'pointerleave'] as const) b.addEventListener(type, release);
      b.addEventListener('contextmenu', e => e.preventDefault());
      this.buttons.append(b);
    };
    button('Use', 'interact');
    button('Block', 'block');
    button('Chat', null, () => actions.chat());
    button('Menu', null, () => actions.menu());
    document.body.append(this.buttons);

    const onMode = (active: boolean) => this.setActive(active);
    touchMode.changed.add(onMode);
    this.off.push(() => touchMode.changed.delete(onMode));
    this.setActive(touchMode.active);
  }

  private setActive(active: boolean): void {
    this.buttons.classList.toggle('hidden', !active);
    if (!active) { this.sticks?.destroy(); this.sticks = null; this.left = this.right = null; this.held.clear(); return; }
    if (this.sticks) return;
    const sticks = this.sticks = create({
      zone: this.stage, mode: 'dynamic', multitouch: true, maxNumberOfJoysticks: 2, size: 110,
      color: { front: 'rgba(236, 226, 214, 0.75)', back: 'rgba(20, 12, 14, 0.45)' }, restOpacity: 0.6, fadeTime: 120,
    });
    sticks.on('start', e => {
      const j = e.data as unknown as Joystick;
      const stick: Stick = { uid: j.uid, x: 0, y: 0, force: 0, started: performance.now() / 1000, aimed: false };
      // One stick per half; a second thumb on the same half is ignored.
      if (j.position.x < window.innerWidth / 2) { if (!this.left) this.left = stick; }
      else if (!this.right) this.right = stick;
    });
    sticks.on('move', e => {
      const d = e.data as unknown as MoveData;
      const stick = this.left?.uid === d.instance.uid ? this.left : this.right?.uid === d.instance.uid ? this.right : null;
      if (!stick) return;
      const len = Math.hypot(d.vector.x, d.vector.y) || 1;
      stick.x = d.vector.x / len; stick.y = -d.vector.y / len;
      stick.force = Math.min(1, d.force);
      if (stick.force > DEADZONE) stick.aimed = true;
    });
    sticks.on('end', e => {
      const uid = (e.data as unknown as Joystick).uid;
      if (this.left?.uid === uid) this.left = null;
      if (this.right?.uid === uid) {
        if (!this.right.aimed && performance.now() / 1000 - this.right.started < TAP_SECONDS) this.tapped = true;
        this.right = null;
      }
    });
  }

  /** The movement buttons (the motor's bits) the left stick holds: eight directions, running when pushed to the rim. */
  moveButtons(): number {
    const s = this.left;
    if (!s || s.force < DEADZONE) return 0;
    let b = 0;
    if (s.y < -AXIS) b |= UP;
    if (s.y > AXIS) b |= DOWN;
    if (s.x < -AXIS) b |= LEFT;
    if (s.x > AXIS) b |= RIGHT;
    if (s.force >= RUN_FORCE) b |= RUN;
    return b;
  }

  /** Where the right stick aims, as a screen direction and push (0-1), while it is pushed past the deadzone. */
  aim(): { x: number; y: number; force: number } | null {
    const s = this.right;
    return s && s.force > DEADZONE ? { x: s.x, y: s.y, force: (s.force - DEADZONE) / (1 - DEADZONE) } : null;
  }

  /** The right half was tapped (no aim): attack the way the player faces. */
  takeTap(): boolean { const t = this.tapped; this.tapped = false; return t; }
  down(button: 'interact' | 'block'): boolean { return this.held.has(button); }
  pressed(button: 'interact'): boolean { return this.pressedNow.has(button); }
  endFrame(): void { this.pressedNow.clear(); this.tapped = false; }

  dispose(): void {
    this.off.forEach(f => f());
    this.sticks?.destroy();
    this.buttons.remove();
  }
}
